import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

export function ServicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(0);

  const effectiveCustomerId = isAdmin
    ? selectedCustomerId
      ? Number(selectedCustomerId)
      : null
    : Number(user?.customerId || 0) || null;

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (cancelled || !res.ok) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setCustomers(list);
      setSelectedCustomerId((prev) => prev || (list[0]?.id ? String(list[0].id) : ""));
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    if (search.trim()) query.set("search", search.trim());
    if (effectiveCustomerId) query.set("customerId", String(effectiveCustomerId));
    const path = query.toString() ? `/api/Services?${query}` : "/api/Services";
    const res = await apiGet(path);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load services"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [effectiveCustomerId, search, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const removeRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Delete this service?")
      : window.confirm("Delete this service?");
    if (!confirmed) return;
    setDeletingId(row.id);
    const res = await apiDelete(`/api/Services/${row.id}`);
    setDeletingId(0);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to delete service"), "error");
      return;
    }
    toast.push("Service deleted", "success");
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Services</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage services with optional landing page content.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/services/new")}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Add New
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
          <input
            className={inputClass}
            placeholder="Search name or code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Image</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Code</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Order</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const src = resolveMediaUrl(row.image ?? row.Image);
                  const active = row.isActive ?? row.IsActive ?? true;
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        {src ? (
                          <img src={src} alt="" className="h-12 w-12 rounded-lg border border-slate-200 object-cover" />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.code || "-"}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{row.name || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">{row.order ?? 0}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/services/${row.id}/edit`)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/services/${row.id}/preview`)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => void removeRow(row)}
                            disabled={deletingId === row.id}
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            {deletingId === row.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                      No services found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
